<template>
  <div class="w-full min-h-screen">
    <Navbar />
    <div class="flex flex-1 flex-wrap justify-around px-16 gap-8 py-4 pb-8 lg:py-16">
      <div class="relative w-1/4 md:w-1/5 lg:w-1/5"
           v-for="badge in badges"
           :key="badge.id"
           @mouseenter="badge.isHovered = true"
           @mouseleave="badge.isHovered = false">
        <transition name="fade">
          <div v-show="badge.isHovered"
               class="absolute top-0 -right-10 bg-graybase border border-primary rounded rounded-t text-white p-2 w-3/4 z-50">
            <Typography size="secondary-sm-lite" class="text-center">{{ badge.tooltipText }}</Typography>
          </div>
        </transition>
        <div class="absolute z-10 top-1/3 left-1/2 transform -translate-x-1/2"
             v-if="isClaimable  (badge.id)">
          <button class="h-12 min-w-36 bg-primary text-2xl w-1/2"
                  @click="claimBadge(badge)">
            <Typography size="main-l">Claim</Typography>
          </button>
        </div>
        <div class="flex flex-col gap-2 items-center cursor-pointer z-0"
             @click="openBadgeModal(badge)">
          <div v-if="badge.apecoin && badge.userHasBadge"
              class="absolute left-0 lg:top-4 z-10 lg:left-0 2xl:left-1/4">
            <span v-if="badge.award_apecoin > 0"
                  class="bg-white text-[#013ACD] w-6 h-6 rounded-2xl absolute -right-3 -top-2 font-gotham font-black text-sm leading-3 flex justify-center items-center">{{ badge.award_apecoin }}</span>
            <img src="../assets/images/apecoin_logo.png"
                alt="badge claimed"
                class="w-8 lg:w-10" />
          </div>
          <div v-else-if="badge.apecoin && !badge.userHasBadge"
              class="absolute left-0 lg:top-4 z-10 lg:left-0 2xl:left-1/4">
            <span v-if="badge.award_apecoin > 0"
                  class="bg-[#999999] text-[#002575] w-6 h-6 rounded-2xl absolute -right-3 -top-2 font-gotham font-black text-sm leading-3 flex justify-center items-center">{{ badge.award_apecoin }}</span>
            <img src="../assets/images/apecoin_logo_no_claimed.png"
                alt="badge not claimed"
                class="w-8 lg:w-10" />
          </div>
          <img :src="badge.image" alt="" :class="{'badge-disabled': !badge.userHasBadge}" />
          <Typography size="main-l" class="text-nowrap" :class="{'badge-disabled': !badge.userHasBadge}">{{ badge.title }}</Typography>
        </div>
      </div>
    </div>
    <Modal :value="badgeModal.isOpen" @close="badgeModal.isOpen = false">
      <div class="flex flex-col items-center gap-6">
        <Typography size="main-l" class="!text-5xl lg:!text-7xl text-nowrap">{{ badgeModal.badge?.title }}</Typography>
        <img :src="badgeModal.badge?.image" alt="" class="h-40 lg:h-80" />
        <Typography class="text-center">{{badgeModal.badge?.description}}</Typography>
      </div>
    </Modal>
    <AlbarraModal />
    <MobileFullModal />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Navbar from '@/components/Navbar.vue'
import Typography from '../components/common/Typography.vue'
import Modal from '@/components/modal.vue'
import AlbarraModal from "../components/modals/AlbarraModal.vue";
import MobileFullModal from "../components/mobile-full-modal.vue";
import { useAuthStore } from '../stores/useAuthStore'
import { cmsStore } from '../stores/cmsStore'
import { useModalsStore } from "../stores/useModalsStore";

const assetsUrl = useRuntimeConfig().public.assetsUrl
const cms = cmsStore()
const auth = useAuthStore()
const modalsStore = useModalsStore();
const badges = ref([])
const userBadges = ref([])
const badgeModal = ref({
  isOpen: false,
  badge: null,
})
const badgeNotifications = computed(() => cms.getClaimBadgeNotifications)

const isClaimable = (badgeId) => badgeNotifications.value.some(badge => badge.badge_id === badgeId)

const openBadgeModal = (badge) => {
  if (!badge.userHasBadge) return
  badgeModal.value.isOpen = true
  badgeModal.value.badge = badge
}

const loadBadges = async () => {
  let badgesItems = await cms.loadBadges()
  badgesItems = badgesItems.map((badge, idx) => ({
    ...badge,
    image: `${assetsUrl}/${badge.image}`,
    userHasBadge: false,
    isHovered: false,
  }))
  badges.value = badgesItems
}

const loadUserBadges = async () => {
  const params = {
    filter: {
      user_id: auth.user.directusUser.id,
    },
  }
  const badgesItems = await cms.get('badges_directus_users', params)
  userBadges.value = badgesItems.data
}

const setUserBadges = () => {
  badges.value = badges.value.map(badge => {
    const userBadge = userBadges.value.find(userBadge => userBadge.badge_id === badge.id)
    if (userBadge) {
      badge.userHasBadge = true
    }
    return badge
  })
}

const claimBadge = async (badge) => {
  const assetsUrl = useRuntimeConfig().public.assetsUrl
  const isProd = !assetsUrl.includes('pre')
  const GOT_AXED_BADGE = 8
  const OTHER_PAGE_BADGE = 47
  const TOMAHAWK_BADGE = 46
  const WOOD_COLLECTOR_BADGE = 48
  const THE_RAIL_BADGE_ID = 49
  const HOT_STREAK_BADGE_ID = 52
  const BORING_SECURITY_BADGE = isProd ? 71 : 66
  const HOT_STREAK_100_BADGE_ID = isProd ? 74 : 70
  const VERIFIED_ON_CHAIN_BADGE_ID = 77
  const RILLAZ_BADGE_ID = 82
  const RILLAZ_DEPIX_BADGE_ID = 83
  
  if (badge.id === GOT_AXED_BADGE) {
    const notification = cms.notifications.find(n => n.badge_id === GOT_AXED_BADGE)
    modalsStore.openModal('SUCCESS_CLAIM_GOT_AXED_BADGE', { logs: notification.logs });
  } else if (badge.id === OTHER_PAGE_BADGE) {
    modalsStore.openModal('SUCCESS_CLAIM_OTHER_PAGE_BADGE');
  } else if (badge.id === TOMAHAWK_BADGE) {
    modalsStore.openModal('SUCCESS_CLAIM_TOMAHAWK_BADGE');
  } else if (badge.id === WOOD_COLLECTOR_BADGE) {
    modalsStore.openModal('SUCCESS_CLAIM_WOOD_COLLECTOR_BADGE');
  } else if (badge.id === THE_RAIL_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_THE_RAIL_BADGE');
  } else if (badge.id === HOT_STREAK_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_HOT_STREAK_BADGE');
  } else if (badge.id === BORING_SECURITY_BADGE) {
    modalsStore.openModal('SUCCESS_CLAIM_BORING_SECURITY_BADGE', { badge_id: badge.id });
  } else if (badge.id === HOT_STREAK_100_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_HOT_STREAK_100_BADGE', { badge_id: badge.id });
  } else if (badge.id === VERIFIED_ON_CHAIN_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_VERIFIED_ON_CHAIN_BADGE', { badge_id: badge.id });
  } else if (badge.id === RILLAZ_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_RILLAZ_BADGE', { badge_id: badge.id });
  } else if (badge.id === RILLAZ_DEPIX_BADGE_ID) {
    modalsStore.openModal('SUCCESS_CLAIM_RILLAZ_DEPIX_BADGE', { badge_id: badge.id });
  }
}

onMounted(() => {
  if (auth.isAuthenticated) {
    loadUserBadges()
      .then(loadBadges)
      .then(setUserBadges)
  } else {
    loadBadges()
  }
})
</script>

<style scoped>
.badge-disabled {
  opacity: 25%;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fade-enter-to, .fade-leave-from {
  opacity: 1;
}
</style>
